import { AudioLevelService } from "@/features/audio/interfaces/browser-services.interface";

const NUM_BARS = 12;

export class BrowserAudioLevelService implements AudioLevelService {
    monitorStream(stream: MediaStream, onLevels: (levels: number[]) => void, isMuted: () => boolean): () => void {
        return this.startAnalyser(stream, onLevels, isMuted, false);
    }

    monitorFromUserMedia(onLevels: (levels: number[]) => void, isMuted: () => boolean): () => void {
        let cleanup: (() => void) | null = null;
        let cancelled = false;

        navigator.mediaDevices
            .getUserMedia({ audio: true })
            .then((stream) => {
                if (cancelled) {
                    stream.getTracks().forEach((track) => track.stop());
                    return;
                }

                cleanup = this.startAnalyser(stream, onLevels, isMuted, true);
            })
            .catch((error) => {
                console.error("Error accessing microphone for audio level:", error);
            });

        return () => {
            cancelled = true;
            cleanup?.();
        };
    }

    private startAnalyser(
        stream: MediaStream,
        onLevels: (levels: number[]) => void,
        isMuted: () => boolean,
        stopTracksOnCleanup: boolean
    ): () => void {
        const AudioContextClass =
            window.AudioContext ||
            (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        const audioContext = new AudioContextClass();
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 1024;
        analyser.smoothingTimeConstant = 0.8;

        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);

        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        let animationFrameId = 0;

        const updateLevel = () => {
            if (isMuted()) {
                onLevels(new Array(NUM_BARS).fill(0));
            } else {
                analyser.getByteFrequencyData(dataArray);
                const voiceRange = dataArray.slice(0, Math.min(100, dataArray.length));
                const chunkSize = Math.floor(voiceRange.length / NUM_BARS);
                const levels = new Array(NUM_BARS).fill(0).map((_, index) => {
                    const start = index * chunkSize;
                    const end = start + chunkSize;
                    const chunk = voiceRange.slice(start, end);
                    const max = Math.max(...Array.from(chunk));
                    return Math.min((max / 255) * 3, 1);
                });
                onLevels(levels);
            }

            animationFrameId = requestAnimationFrame(updateLevel);
        };

        updateLevel();

        return () => {
            cancelAnimationFrame(animationFrameId);
            source.disconnect();
            analyser.disconnect();
            audioContext.close().catch(() => undefined);
            if (stopTracksOnCleanup) {
                stream.getTracks().forEach((track) => track.stop());
            }
        };
    }
}

export const browserAudioLevelService = new BrowserAudioLevelService();
